import React, {useEffect, useRef} from 'react'
import { Container, Row, Col } from 'react-bootstrap'

import NumInput from '../common/inputs/NumInput'
import SelectInput from '../common/inputs/SelectInput'
import TextInput from '../common/inputs/TextInput'
import ToggleInput from './inputs/ToggleInput'
import MultiSelectInput from './inputs/MultiSelectInput'
import CompactSelect from './inputs/CompactSelect'
import CompactMultiSelect from './inputs/CompactMultiSelect'
import CalculateBtn from './CalculateBtn'

import { exchangeIOLModelNames, exchangeReasons, getAConstant } from '../helpers/enums/IOLExchangeModels'
import { calculateMRSE, MIN_SAFE_POWER, MAX_SAFE_POWER,
         CURRENT_POWER_MIN, CURRENT_POWER_MAX, POWER_STEP, MAX_DECIMALS,
         isSameModelChange, hasIdenticalManualAConstants, getToricBlocker } from './IOLExchangeCalculation'

// Explanatory helper text, shown in the Large layout. CSS hides it entirely
// in Compact (.iolx-compact .iolx-note{display:none}) — Large is the
// default view, so the full explanation lives there.
function Note({text}){
    return (
        <span className='iolx-note'>{text}</span>
    )
}

export default function Input({
    validInput, setValidInput,
    onCalculate,
    compact,

    //Exchange setup
    F11VS, setF11VS,    //Change IOL model?
    F12VS, setF12VS,    //A-constant source

    //Current IOL
    F21VS, setF21VS,    //Current IOL power
    F22VS, setF22VS,    //Current IOL manufacturer / model
    F23VS, setF23VS,    //Current IOL A-constant
    F24VS, setF24VS,    //Current IOL position
    F25VS, setF25VS,    //Current IOL cylinder (toric)
    F26VS, setF26VS,    //Current IOL axis of marks (toric)
    F27VS, setF27VS,    //Is the implanted IOL toric? (toric)

    //Current manifest refraction
    F31VS, setF31VS,    //Sphere
    F32VS, setF32VS,    //Cylinder
    F33VS, setF33VS,    //MRSE (computed, read only)
    F34VS, setF34VS,    //Axis (optional)

    //Reason for exchange
    F41VS, setF41VS,    //Reasons (multi-select)
    F42VS, setF42VS,    //Other (free text)

    //New IOL
    F51VS, setF51VS,    //New IOL manufacturer / model
    F52VS, setF52VS,    //New IOL A-constant
    F53VS, setF53VS,    //New IOL position
    F54VS, setF54VS,    //Target MRSE
    F55VS, setF55VS,    //Use toric for exchange?

    //Case type
    F61VS, setF61VS,    //Test / real patient calculation
}) {

    const changeModel = (F11VS[0] === 'Yes');
    const fromModelList = (F12VS[0] === 'Model list');
    const otherReason = F41VS[0].includes('Other');
    // The workbook refuses the first and warns about the second (IOLX!F15).
    const sameModelChange = isSameModelChange(changeModel, fromModelList, F22VS[0], F51VS[0]);
    const identicalManualAConstants = hasIdenticalManualAConstants(changeModel, fromModelList, F23VS[0], F52VS[0]);
    // Toric block (Dr. Buonsanti's workbook): asks whether the implanted lens is toric,
    // and if so its cylinder and marks; the refraction axis becomes required.
    const toric = (F55VS[0] === 'Yes');
    const toricBlocker = getToricBlocker(F55VS[0], F21VS[0], F53VS[0]);
    const implantedToric = (F27VS[0] === 'Yes');
    const nonToricImplant = (F27VS[0] === 'No');

    // "Is the implanted IOL toric?" (Peter and Dante, 15 Oct): typing a 0 cylinder for a
    // non-toric implant was not intuitive. "No" sets the cylinder to 0 and hides both
    // fields - no 0° on screen, which would read as a real lens orientation. "Yes" starts
    // the cylinder empty, so it is typed on purpose rather than left at the 0 of "No".
    // Set together with the answer, in the same event, so no render sees one without
    // the other.
    function setImplantedToric(VS){
        setF27VS(VS);
        if(VS[0] === 'No'){
            setF25VS(["0", 1]);
        }else if(VS[0] === 'Yes' && F27VS[0] !== 'Yes'){
            setF25VS(["", -1]);
        }
    }

    // MRSE is computed, never entered (SRS 1.1).
    useEffect(() => {
        let mrse = calculateMRSE(F31VS[0], F32VS[0]);
        setF33VS([mrse, (mrse === "")? -1 : 1]);
    }, [F31VS[0], F32VS[0]])

    // Selecting a model fills its A-constant (SRS 1.1.1).
    //
    // The validity flag MUST be in the dependency list, not just the value.
    // SelectInput's onChange does setVS([value, VS[1]]) — it carries the previous
    // validity — and only its own effect promotes that to 1 on the next render. So
    // on the first selection this effect would otherwise run once, while the flag
    // was still -1, fail the guard, and never re-run because the value no longer
    // changes. The A-constant then stayed empty forever and the form could never
    // validate. Same reasoning for the two effects below.
    // getAConstant returns "" when the name matches no model in the list, so the
    // validity flag has to follow the lookup instead of being hardcoded to 1.
    // Otherwise an empty A-constant gets marked valid: the mirror effect below
    // copies the CURRENT model into F51VS placeholder and all, SelectInput judges
    // that against its OWN ("Select replacement IOL model") placeholder, finds
    // them different and marks it valid — so this effect fired with a name no
    // model matches and silently stored "" as valid. The form then passed
    // validation with no replacement A-constant at all, and the [F1] A-constant
    // adjustment quietly evaluated to zero while the UI still said "IOL model
    // changes".
    useEffect(() => {
        if(fromModelList && F22VS[1] === 1){
            let aConstant = getAConstant(F22VS[0]);
            setF23VS([aConstant, (aConstant === "")? -1 : 1]);
        }
    }, [F22VS[0], F22VS[1], fromModelList])

    useEffect(() => {
        if(fromModelList && F51VS[1] === 1){
            let aConstant = getAConstant(F51VS[0]);
            setF52VS([aConstant, (aConstant === "")? -1 : 1]);
        }
    }, [F51VS[0], F51VS[1], fromModelList])

    // When the model does not change, the new lens keeps the current constant.
    // Copies the validity flags too, so they have to be watched as well: the new
    // IOL A-constant field is read only, which means NumInput never validates it
    // and this mirror is the only thing that can.
    useEffect(() => {
        if(!changeModel){
            setF51VS([F22VS[0], F22VS[1]]);
            setF52VS([F23VS[0], F23VS[1]]);
        }
    }, [changeModel, F22VS[0], F22VS[1], F23VS[0], F23VS[1]])

    // Switching "Change IOL model?" to Yes starts the new lens empty (Peter,
    // 29 Sep): the mirror above left the current model and A-constant in it,
    // which read as if the replacement were already chosen. Only on the switch
    // itself, so a model picked afterwards is never wiped.
    const prevChangeModel = useRef(changeModel);
    useEffect(() => {
        if(changeModel && !prevChangeModel.current){
            setF51VS(["Select replacement IOL model", -1]);
            setF52VS(["", -1]);
        }
        prevChangeModel.current = changeModel;
    }, [changeModel])

    // With no cylinder in the implanted lens (answer "No") its marks do not matter:
    // they take the refraction axis, which is what the calculation uses
    // (Dr. Buonsanti, 27 Sep). For a toric implant the marks start empty, so they are
    // typed on purpose rather than inherited. Keyed on the answer, not on the cylinder
    // value: a 0 typed for a toric implant is refused, and for one render it would
    // otherwise look like "no cylinder" and wipe the marks already typed.
    const prevNonToricImplant = useRef(nonToricImplant);
    useEffect(() => {
        if(nonToricImplant){
            setF26VS([F34VS[0], F34VS[1]]);
        }else if(prevNonToricImplant.current){
            setF26VS(["", -1]);
        }
        prevNonToricImplant.current = nonToricImplant;
    }, [nonToricImplant, F34VS[0], F34VS[1]])

    // Form validity.
    useEffect(() => {
        let valid = (
            F11VS[1] === 1 &&
            F12VS[1] === 1 &&
            F21VS[1] === 1 &&
            F23VS[1] === 1 &&
            F24VS[1] === 1 &&
            F31VS[1] === 1 &&
            F32VS[1] === 1 &&
            F33VS[1] === 1 &&
            F52VS[1] === 1 &&
            F53VS[1] === 1 &&
            F54VS[1] === 1 &&
            F55VS[1] === 1 &&
            F61VS[1] === 1
        );
        //"Other" ticked requires the free text (SRS 1.3).
        if(valid && otherReason && F42VS[1] !== 1) valid = false;
        //Changing to the same model is refused, as in the workbook.
        if(sameModelChange) valid = false;
        //Axis (F34) is optional, unless the calculation is toric: then the refraction
        //axis is needed, and whether the implanted lens is toric. A toric implant needs
        //its cylinder and marks; a non-toric one has its cylinder set to 0 and no marks.
        if(toric && (F34VS[1] !== 1 || F27VS[1] !== 1 || F25VS[1] !== 1
                     || (implantedToric && F26VS[1] !== 1) || toricBlocker !== null)){
            valid = false;
        }
        setValidInput(valid);
    }, [F11VS[1], F12VS[1], F21VS[1], F23VS[1], F24VS[1], F31VS[1], F32VS[1], F33VS[1],
        F42VS[1], F52VS[1], F53VS[1], F54VS[1], F55VS[1], F61VS[1], otherReason, sameModelChange,
        toric, F34VS[1], F25VS[1], F26VS[1], F27VS[1], implantedToric, toricBlocker])

    const powerOutOfRange = (
        F21VS[1] === 1 &&
        (parseFloat(F21VS[0]) < MIN_SAFE_POWER || parseFloat(F21VS[0]) > MAX_SAFE_POWER)
    );

    return (
    <Container className='iolx-form-container'>

        {/* EXCHANGE SETUP */}
        <Row className='iolx-section'>
            <b className='title2 iolx-section-title'>Exchange setup</b>
            <Col xs={12} sm={6}>
                {(compact)?
                    <CompactSelect
                        label="Change IOL model?"
                        VS={F11VS} setVS={setF11VS}
                        placeholder="Select"
                        options={[{value: 'Yes', label: 'Yes'}, {value: 'No', label: 'No'}]}
                    />
                :
                    <ToggleInput
                        label="Change IOL model?"
                        VS={F11VS} setVS={setF11VS}
                        options={[{value: 'Yes', label: 'Yes'}, {value: 'No', label: 'No'}]}
                    />
                }
            </Col>
            <Col xs={12} sm={6}>
                {(compact)?
                    <CompactSelect
                        label="A-constant source"
                        VS={F12VS} setVS={setF12VS}
                        placeholder="Select"
                        options={[{value: 'Model list', label: 'Model list'},
                                  {value: 'Manual', label: 'Manual'}]}
                    />
                :
                    <ToggleInput
                        label="A-constant source"
                        VS={F12VS} setVS={setF12VS}
                        options={[{value: 'Model list', label: 'Model list'},
                                  {value: 'Manual', label: 'Manual'}]}
                    />
                }
            </Col>
            {/* SRS 1.1.1: the list carries optimised constants, and the UI says so. */}
            <Col xs={12} style={{display: (fromModelList)? null : 'none'}}>
                <Note text="Model-list A-constants are optimised SRK/T constants and may differ from the manufacturer's nominal values."/>
            </Col>
        </Row>

        <div className='separator iolx-separator'></div>

        {/* CURRENT IOL */}
        <Row className='iolx-section'>
            <b className='title2 iolx-section-title'>Current (implanted) IOL</b>
            <Col xs={12} sm={4} className='iolx-numeric-col'>
                <NumInput VS={F21VS} setVS={setF21VS}
                    label="Power (D):"
                    placeholder="e.g. 22.0"
                    min={CURRENT_POWER_MIN} max={CURRENT_POWER_MAX} step={POWER_STEP}
                    enforceStep={true} maxDecimals={MAX_DECIMALS}
                    readonly={false}
                />
            </Col>
            <Col xs={12} sm={8}>
                {/* The workbook only models a lens that is in the bag now ([F3]
                    converts bag to sulcus). Sulcus as the CURRENT position stays
                    disabled until the model for it is defined - the engine refuses it. */}
                <ToggleInput
                    label="Position:"
                    VS={F24VS} setVS={setF24VS}
                    options={[{value: 'Bag', label: 'Bag'},
                              {value: 'Sulcus', label: 'Sulcus', disabled: true, note: 'not modelled'}]}
                />
            </Col>
            <Col xs={12} sm={compact? 8 : 12}
            style={{display: (fromModelList)? null : 'none'}}>
                <SelectInput VS={F22VS} setVS={setF22VS}
                    label="Manufacturer / Model:"
                    placeholder={"Select implanted IOL model"}
                    options={exchangeIOLModelNames}
                />
            </Col>
            <Col xs={12} sm={4} className='iolx-numeric-col'>
                <NumInput VS={F23VS} setVS={setF23VS}
                    label="A-constant:"
                    placeholder="e.g. 119.00"
                    min={100} max={130}
                    readonly={fromModelList}
                />
            </Col>
            {(toric)?
            <>
                {/* Toric block, Calculadora!B12:B13 of Dr. Buonsanti's workbook. On its
                    own line: the question, then a toric implant's cylinder and marks. */}
                <div className='w-100'></div>
                <Col xs={12} sm={4}>
                    {(compact)?
                        <CompactSelect
                            label="Is the implanted IOL toric?"
                            VS={F27VS} setVS={setImplantedToric}
                            placeholder="Select"
                            options={[{value: 'Yes', label: 'Yes'}, {value: 'No', label: 'No'}]}
                        />
                    :
                        <ToggleInput
                            label="Is the implanted IOL toric?"
                            VS={F27VS} setVS={setImplantedToric}
                            options={[{value: 'Yes', label: 'Yes'}, {value: 'No', label: 'No'}]}
                        />
                    }
                </Col>
                {(implantedToric)?
                <>
                    {/* A toric lens has a cylinder: 0 is refused here (answer "No" instead). */}
                    <Col xs={12} sm={4} className='iolx-numeric-col'>
                        <NumInput VS={F25VS} setVS={setF25VS}
                            label="Cylinder (D):"
                            placeholder="e.g. 2.25"
                            min={0.01} max={10} maxDecimals={MAX_DECIMALS}
                            readonly={false}
                        />
                    </Col>
                    <Col xs={12} sm={4} className='iolx-numeric-col'>
                        <NumInput VS={F26VS} setVS={setF26VS}
                            label="Axis of marks (°):"
                            placeholder="0–180"
                            min={0} max={180}
                            readonly={false}
                        />
                    </Col>
                    <Col xs={12}>
                        <Note text="Toric implant: enter its power above as the spherical equivalent (SEQ)."/>
                    </Col>
                </>
                : null}
            </>
            : null}
            <Col xs={12}>
                <span className='iolx-warning'
                style={{display: (powerOutOfRange)? null : 'none'}}>
                    Power outside the usual {MIN_SAFE_POWER}–{MAX_SAFE_POWER} D range:
                    predictions are extrapolated.
                </span>
                <span className='iolx-warning'
                style={{display: (F21VS[1] === 0)? null : 'none'}}>
                    The current IOL power must be {CURRENT_POWER_MIN.toFixed(1)}–{CURRENT_POWER_MAX.toFixed(1)} D
                    in {POWER_STEP} D steps.
                </span>
            </Col>
        </Row>

        <div className='separator iolx-separator'></div>

        {/* CURRENT REFRACTION */}
        <Row className='iolx-section'>
            <b className='title2 iolx-section-title'>Current manifest refraction</b>
            <Col xs={12} sm={3} className='iolx-numeric-col'>
                <NumInput VS={F31VS} setVS={setF31VS}
                    label="Sphere:"
                    placeholder="e.g. -1.50"
                    min={-30} max={30} step={0.25} maxDecimals={MAX_DECIMALS}
                    readonly={false}
                />
            </Col>
            <Col xs={12} sm={3} className='iolx-numeric-col'>
                <NumInput VS={F32VS} setVS={setF32VS}
                    label="Cylinder:"
                    placeholder="e.g. -0.75"
                    min={-15} max={15} step={0.25} maxDecimals={MAX_DECIMALS}
                    readonly={false}
                />
            </Col>
            <Col xs={12} sm={3} className='iolx-numeric-col'>
                <NumInput VS={F34VS} setVS={setF34VS}
                    label="Axis:"
                    placeholder={(toric)? "0–180" : "Optional"}
                    min={0} max={180} step={1}
                    readonly={false}
                />
            </Col>
            <Col xs={12} sm={3} className='iolx-numeric-col'>
                <NumInput VS={F33VS} setVS={setF33VS}
                    label={compact? "MRSE:" : "Current MRSE (D):"}
                    placeholder="Sphere + Cylinder / 2"
                    readonly={true}
                />
            </Col>
            <Col xs={12}>
                <Note text="MRSE is computed, not entered. The cylinder sign matters."/>
            </Col>
        </Row>

        <div className='separator iolx-separator'></div>

        {/* REASON FOR EXCHANGE */}
        <Row className='iolx-section'>
            <b className='title2 iolx-section-title'>Reason for exchange</b>
            <Col xs={12}>
                {(compact)?
                    <CompactMultiSelect
                        label=""
                        options={exchangeReasons}
                        VS={F41VS} setVS={setF41VS}
                    />
                :
                    <MultiSelectInput
                        label=""
                        options={exchangeReasons}
                        VS={F41VS} setVS={setF41VS}
                    />
                }
            </Col>
            <Col xs={12}
            style={{display: (otherReason)? null : 'none'}}>
                <TextInput VS={F42VS} setVS={setF42VS} label={"Other:"}
                    placeholder="Describe the reason"/>
            </Col>
            <Col xs={12}>
                <Note text="Recorded with the case; not used in the calculation."/>
            </Col>
        </Row>

        <div className='separator iolx-separator'></div>

        {/* NEW IOL */}
        <Row className='iolx-section'>
            <b className='title2 iolx-section-title'>New (replacement) IOL</b>
            <Col xs={12} sm={compact? 8 : 12}
            style={{display: (fromModelList && changeModel)? null : 'none'}}>
                <SelectInput VS={F51VS} setVS={setF51VS}
                    label="Manufacturer / Model:"
                    placeholder={"Select replacement IOL model"}
                    options={exchangeIOLModelNames}
                />
            </Col>
            <Col xs={12} sm={4} className='iolx-numeric-col'>
                <NumInput VS={F52VS} setVS={setF52VS}
                    label="A-constant:"
                    placeholder="e.g. 119.30"
                    min={100} max={130}
                    readonly={fromModelList || !changeModel}
                />
            </Col>
            <Col xs={12} sm={8}>
                <ToggleInput
                    label="Position:"
                    VS={F53VS} setVS={setF53VS}
                    options={[{value: 'Bag', label: 'Bag'},
                              {value: 'Sulcus', label: 'Sulcus'},
                              {value: 'Scleral fixation', label: 'Scleral fixation', disabled: true}]}
                />
            </Col>
            <Col xs={12}>
                {/* Workbook IOLX!F15, the refusal and the warning, in its own words. */}
                <span className='iolx-warning'
                style={{display: (sameModelChange)? null : 'none'}}>
                    Same model as the implanted lens: set "Change IOL model?" to No,
                    or select a different new IOL model.
                </span>
                <span className='iolx-warning'
                style={{display: (identicalManualAConstants)? null : 'none'}}>
                    The manual current and new A-constants are identical, so the
                    model-change A-shift is 0.00 D. Confirm this is intentional.
                </span>
                <span style={{display: (F53VS[0] === 'Sulcus')? null : 'none'}}>
                    <Note text="The sulcus adjustment is empiric and not fixation-specific. Optic capture is out of scope."/>
                </span>
            </Col>
            <Col xs={12} sm={4} className='iolx-numeric-col'>
                <NumInput VS={F54VS} setVS={setF54VS}
                    label="Target MRSE (D):"
                    placeholder="0.00"
                    min={-15} max={15} step={0.25} maxDecimals={MAX_DECIMALS}
                    readonly={false}
                />
            </Col>
            <Col xs={12} sm={compact? 8 : 12}>
                {(compact)?
                    <CompactSelect
                        label="Use toric for exchange?"
                        VS={F55VS} setVS={setF55VS}
                        options={[{value: 'Yes', label: 'Yes'},
                                  {value: 'No', label: 'No'},
                                  {value: 'Not sure', label: 'Not sure', disabled: true}]}
                    />
                :
                    <ToggleInput
                        label="Use toric for exchange?"
                        VS={F55VS} setVS={setF55VS}
                        options={[{value: 'Yes', label: 'Yes'},
                                  {value: 'No', label: 'No'},
                                  {value: 'Not sure', label: 'Not sure', disabled: true, note: 'coming soon'}]}
                    />
                }
            </Col>
            <Col xs={12}>
                <span className='iolx-warning'
                style={{display: (toricBlocker)? null : 'none'}}>
                    {toricBlocker}
                </span>
                <Note text="Candidate powers assume uniform 0.5 D availability — confirm against the manufacturer's catalogue."/>
            </Col>
        </Row>

        <div className='separator iolx-separator'></div>

        {/* CASE TYPE */}
        <Row className='iolx-section'>
            <b className='title2 iolx-section-title'>Case type</b>
            <Col xs={12}>
                {(compact)?
                    <CompactSelect
                        label=""
                        VS={F61VS} setVS={setF61VS}
                        placeholder="Select case type"
                        options={[{value: 'Test', label: 'This is a test'},
                                  {value: 'Real', label: 'This is a real patient calculation'}]}
                    />
                :
                    <ToggleInput
                        label=""
                        VS={F61VS} setVS={setF61VS}
                        options={[{value: 'Test', label: 'This is a test'},
                                  {value: 'Real', label: 'This is a real patient calculation'}]}
                    />
                }
            </Col>
            <Col xs={12}>
                <Note text="Required before calculating."/>
            </Col>
        </Row>

        {/* In Compact this button is rendered by the parent instead, as a
            floating overlay outside the scrolling form area — see
            IOLExchangeCalculator.js. Nesting it inside a scrolling
            container is exactly what made "flush to the box edge" via
            negative margins get clipped, no matter the offset values. */}
        {(!compact)?
        <Row className='iolx-calculate-row'>
            <CalculateBtn
                validInput={validInput}
                onClick={onCalculate}
            />
        </Row>
        : null}
    </Container>
    )
}
